import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import EmptyPage from "components/common/EmptyPage";
import VenueFormDialog from "components/venues/VenueFormDialog";
import performRequest from "network/perform-request";
import {
  leagueCustomerRefereeRoute,
  leagueCustomerRefereesRoute,
  leagueCustomerVenueFieldRoute,
  leagueCustomerVenueFieldsRoute,
  leagueCustomerVenueRoute,
  leagueCustomerVenuesRoute,
} from "network/api-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import CompetitionEstablishments from "components/pages/Competition/CompetitionEstablishments";
import GestionTable from "components/pages/Gestion/GestionTable";

const VENUES_TAB = "venues";
const REFEREES_TAB = "referees";

const emptyRefereeForm = { name: "" };

const GestionVenues = ({ embedded = false, competitionSectionProps }) => {
  const { customerId, customerName } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState(VENUES_TAB);
  const [venues, setVenues] = useState([]);
  const [referees, setReferees] = useState([]);
  const [loadingVenues, setLoadingVenues] = useState(true);
  const [loadingReferees, setLoadingReferees] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [venueDialogOpen, setVenueDialogOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState(null);
  const [form, setForm] = useState(emptyRefereeForm);
  const [saving, setSaving] = useState(false);
  const [localesReloadKey, setLocalesReloadKey] = useState(0);
  const [fieldDialog, setFieldDialog] = useState(null);
  const [fieldName, setFieldName] = useState("");

  const handleError = useCallback(
    (error) => {
      enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
        variant: "error",
      });
    },
    [enqueueSnackbar, intl],
  );

  const loadVenues = useCallback(() => {
    if (!customerId) return;
    setLoadingVenues(true);
    performRequest("GET", leagueCustomerVenuesRoute(customerId), {}, true, user)
      .then((response) => setVenues(Array.isArray(response.data) ? response.data : []))
      .catch((error) => {
        handleError(error);
        setVenues([]);
      })
      .finally(() => setLoadingVenues(false));
  }, [customerId, handleError, user]);

  const loadReferees = useCallback(() => {
    if (!customerId) return;
    setLoadingReferees(true);
    performRequest("GET", leagueCustomerRefereesRoute(customerId), {}, true, user)
      .then((response) => setReferees(Array.isArray(response.data) ? response.data : []))
      .catch((error) => {
        handleError(error);
        setReferees([]);
      })
      .finally(() => setLoadingReferees(false));
  }, [customerId, handleError, user]);

  useEffect(() => {
    loadVenues();
    loadReferees();
  }, [loadReferees, loadVenues]);

  const handleSaveReferee = () => {
    if (!customerId || !form.name.trim()) return;
    setSaving(true);
    performRequest("POST", leagueCustomerRefereesRoute(customerId), { name: form.name.trim() }, true, user)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.referees.created" }), { variant: "success" });
        setDialogOpen(false);
        setForm(emptyRefereeForm);
        loadReferees();
      })
      .catch(handleError)
      .finally(() => setSaving(false));
  };

  const handleSaveVenue = (formData) => {
    if (!customerId) return;
    setSaving(true);
    const isEdit = Boolean(editingVenue?.id);
    const route = isEdit
      ? leagueCustomerVenueRoute(customerId, editingVenue.id)
      : leagueCustomerVenuesRoute(customerId);
    performRequest(isEdit ? "POST" : "POST", route, formData, true, user)
      .then(() => {
        enqueueSnackbar(
          intl.formatMessage({ id: isEdit ? "gestion.venues.updated" : "gestion.venues.createdAndLinked" }),
          { variant: "success" },
        );
        // A new venue is also linked to the Gestión competition: make the Locales section refetch.
        if (!isEdit) setLocalesReloadKey((key) => key + 1);
        setVenueDialogOpen(false);
        setEditingVenue(null);
        loadVenues();
      })
      .catch(handleError)
      .finally(() => setSaving(false));
  };

  const toggleVenueArchive = (venue) => {
    if (!customerId) return;
    performRequest(
      "PATCH",
      leagueCustomerVenueRoute(customerId, venue.id),
      { archived: !venue.archived },
      true,
      user,
    )
      .then(() => loadVenues())
      .catch(handleError);
  };

  const addFieldToVenue = (venue) => {
    setFieldDialog({ venue, field: null });
    setFieldName("");
  };

  const openRenameField = (venue, field) => {
    setFieldDialog({ venue, field });
    setFieldName(field.name || "");
  };

  const saveField = () => {
    if (!customerId || !fieldDialog || !fieldName.trim()) return;
    const { venue, field } = fieldDialog;
    const name = fieldName.trim();
    const request = field
      ? performRequest("PATCH", leagueCustomerVenueFieldRoute(customerId, venue.id, field.id), { name }, true, user)
      : performRequest("POST", leagueCustomerVenueFieldsRoute(customerId, venue.id), { name }, true, user);
    setSaving(true);
    request
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.venues.fieldSaved" }), { variant: "success" });
        setFieldDialog(null);
        loadVenues();
      })
      .catch(handleError)
      .finally(() => setSaving(false));
  };

  const toggleFieldArchive = (venue, field) => {
    if (!customerId) return;
    performRequest(
      "PATCH",
      leagueCustomerVenueFieldRoute(customerId, venue.id, field.id),
      { archived: !field.archived },
      true,
      user,
    )
      .then(() => loadVenues())
      .catch(handleError);
  };

  const deleteField = (venue, field) => {
    if (!customerId || !window.confirm(intl.formatMessage({ id: "gestion.venues.fieldConfirmDelete" }))) return;
    performRequest("DELETE", leagueCustomerVenueFieldRoute(customerId, venue.id, field.id), {}, true, user)
      .then((response) => {
        const deleted = Boolean(response.data?.deleted);
        enqueueSnackbar(
          intl.formatMessage({ id: deleted ? "gestion.venues.fieldDeleted" : "gestion.venues.fieldArchivedInstead" }),
          { variant: deleted ? "success" : "info" },
        );
        loadVenues();
      })
      .catch(handleError);
  };

  const toggleRefereeArchive = (referee) => {
    if (!customerId) return;
    performRequest(
      "PATCH",
      leagueCustomerRefereeRoute(customerId, referee.id),
      { archived: !referee.archived },
      true,
      user,
    )
      .then(() => loadReferees())
      .catch(handleError);
  };

  const deleteReferee = (referee) => {
    if (!customerId || !window.confirm(intl.formatMessage({ id: "gestion.referees.confirmDelete" }))) return;
    performRequest("DELETE", leagueCustomerRefereeRoute(customerId, referee.id), {}, true, user)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.referees.deleted" }), { variant: "success" });
        loadReferees();
      })
      .catch(handleError);
  };

  if (!customerId) return null;

  const venueFields = (venue) => venue.fields || venue.spaces || [];

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", p: embedded ? 0 : { xs: 3, md: 4 } }}>
      {!embedded ? (
        <RevaText variant="h4">
          {intl.formatMessage({ id: "gestion.venues.title" }, { name: customerName || "" })}
        </RevaText>
      ) : null}

      <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mt: 2, mb: 2 }}>
        <Tab value={VENUES_TAB} label={intl.formatMessage({ id: "gestion.venues.tabVenues" })} />
        <Tab value={REFEREES_TAB} label={intl.formatMessage({ id: "gestion.venues.tabReferees" })} />
      </Tabs>

      {tab === VENUES_TAB && (
        <>
          {competitionSectionProps ? (
            <Box sx={{ mb: 4 }}>
              <CompetitionEstablishments {...competitionSectionProps} hideTitle reloadKey={localesReloadKey} />
            </Box>
          ) : null}
          <Stack direction="row" sx={{ mb: 2, justifyContent: "space-between", alignItems: "center", gap: 2 }}>
            <Box>
              <RevaText variant="h6" sx={{ mb: 0.5 }}>
                {intl.formatMessage({ id: "gestion.venues.fieldsSection" })}
              </RevaText>
              <RevaText variant="body2" color="text.secondary">
                {intl.formatMessage({ id: "gestion.venues.hint" })}
              </RevaText>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingVenue(null);
                setVenueDialogOpen(true);
              }}
            >
              {intl.formatMessage({ id: "gestion.venues.create" })}
            </Button>
          </Stack>
          {loadingVenues ? (
            <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Box>
          ) : venues.length === 0 ? (
            <EmptyPage text={intl.formatMessage({ id: "gestion.venues.empty" })} />
          ) : (
            venues.map((venue) => (
              <Box key={venue.id} sx={{ mb: 3 }}>
                <Stack direction="row" sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <RevaText variant="h6">
                    {venue.name}
                    {(!venue.active || venue.archived) && ` (${intl.formatMessage({ id: "gestion.venues.inactive" })})`}
                  </RevaText>
                  <Chip
                    size="small"
                    label={intl.formatMessage({
                      id: venue.bookable ? "gestion.venues.bookable" : "gestion.venues.custom",
                    })}
                  />
                  {venue.can_edit && (
                    <Tooltip title={intl.formatMessage({ id: "gestion.venues.edit" })}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingVenue(venue);
                          setVenueDialogOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={intl.formatMessage({ id: venue.archived ? "gestion.unarchive" : "gestion.archive" })}>
                    <IconButton size="small" onClick={() => toggleVenueArchive(venue)}>
                      {venue.archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  {venue.can_edit && (
                    <Button size="small" onClick={() => addFieldToVenue(venue)}>
                      {intl.formatMessage({ id: "gestion.venues.addField" })}
                    </Button>
                  )}
                </Stack>
                {venue.address && (
                  <RevaText variant="body2" color="text.secondary">
                    {venue.address}
                  </RevaText>
                )}
                <Box sx={{ mt: 1 }}>
                  <GestionTable
                    emptyMessage={intl.formatMessage({ id: "gestion.venues.noSpaces" })}
                    headers={[
                      {
                        key: "name",
                        title: intl.formatMessage({ id: "gestion.venues.space" }),
                        renderCell: (value, row) => (
                          <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                            <RevaText>{value}</RevaText>
                            {row.archived && (
                              <Chip
                                size="small"
                                label={intl.formatMessage({ id: "gestion.venues.fieldArchivedChip" })}
                              />
                            )}
                          </Stack>
                        ),
                      },
                      ...(venue.can_edit
                        ? [
                            {
                              key: "actions",
                              title: intl.formatMessage({ id: "gestion.actions" }),
                              align: "right",
                              renderCell: (_value, row) => (
                                <>
                                  <Tooltip title={intl.formatMessage({ id: "gestion.venues.fieldEdit" })}>
                                    <IconButton
                                      size="small"
                                      aria-label={intl.formatMessage({ id: "gestion.venues.fieldEdit" })}
                                      onClick={() => openRenameField(venue, row.field)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip
                                    title={intl.formatMessage({
                                      id: row.archived ? "gestion.unarchive" : "gestion.archive",
                                    })}
                                  >
                                    <IconButton
                                      size="small"
                                      aria-label={intl.formatMessage({
                                        id: row.archived ? "gestion.unarchive" : "gestion.archive",
                                      })}
                                      onClick={() => toggleFieldArchive(venue, row.field)}
                                    >
                                      {row.archived ? (
                                        <UnarchiveIcon fontSize="small" />
                                      ) : (
                                        <ArchiveIcon fontSize="small" />
                                      )}
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title={intl.formatMessage({ id: "gestion.venues.fieldDelete" })}>
                                    <IconButton
                                      size="small"
                                      aria-label={intl.formatMessage({ id: "gestion.venues.fieldDelete" })}
                                      onClick={() => deleteField(venue, row.field)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </>
                              ),
                            },
                          ]
                        : []),
                    ]}
                    rows={venueFields(venue).map((field) => ({
                      id: field.id,
                      name: field.name || `#${field.id}`,
                      archived: Boolean(field.archived),
                      field,
                    }))}
                  />
                </Box>
              </Box>
            ))
          )}
        </>
      )}

      {tab === REFEREES_TAB && (
        <>
          <Stack direction="row" sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
              {intl.formatMessage({ id: "gestion.referees.create" })}
            </Button>
          </Stack>
          {loadingReferees ? (
            <Box display="flex" justifyContent="center" sx={{ py: 6 }}>
              <CircularProgress />
            </Box>
          ) : referees.length === 0 ? (
            <EmptyPage text={intl.formatMessage({ id: "gestion.referees.empty" })} />
          ) : (
            <GestionTable
              headers={[
                { key: "name", title: intl.formatMessage({ id: "gestion.referees.name" }) },
                { key: "status", title: intl.formatMessage({ id: "gestion.teams.status" }) },
                {
                  key: "actions",
                  title: intl.formatMessage({ id: "gestion.actions" }),
                  align: "right",
                  renderCell: (_value, referee) => (
                    <>
                      <Tooltip
                        title={intl.formatMessage({
                          id: referee.archived ? "gestion.unarchive" : "gestion.archive",
                        })}
                      >
                        <IconButton size="small" onClick={() => toggleRefereeArchive(referee)}>
                          {referee.archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={intl.formatMessage({ id: "gestion.referees.delete" })}>
                        <IconButton size="small" onClick={() => deleteReferee(referee)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ),
                },
              ]}
              rows={referees.map((referee) => ({
                ...referee,
                status: referee.archived
                  ? intl.formatMessage({ id: "gestion.archived" })
                  : intl.formatMessage({ id: "gestion.active" }),
              }))}
            />
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{intl.formatMessage({ id: "gestion.referees.create" })}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            sx={{ mt: 1 }}
            label={intl.formatMessage({ id: "gestion.referees.name" })}
            value={form.name}
            onChange={(event) => setForm({ name: event.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{intl.formatMessage({ id: "league.planillero.cancel" })}</Button>
          <Button variant="contained" onClick={handleSaveReferee} disabled={saving || !form.name.trim()}>
            {intl.formatMessage({ id: "league.planillero.save" })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(fieldDialog)} onClose={() => setFieldDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {intl.formatMessage({ id: fieldDialog?.field ? "gestion.venues.fieldEdit" : "gestion.venues.addField" })}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            sx={{ mt: 1 }}
            label={intl.formatMessage({ id: "gestion.venues.fieldName" })}
            value={fieldName}
            onChange={(event) => setFieldName(event.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFieldDialog(null)}>{intl.formatMessage({ id: "league.planillero.cancel" })}</Button>
          <Button variant="contained" onClick={saveField} disabled={saving || !fieldName.trim()}>
            {intl.formatMessage({ id: "league.planillero.save" })}
          </Button>
        </DialogActions>
      </Dialog>

      <VenueFormDialog
        open={venueDialogOpen}
        venue={editingVenue}
        saving={saving}
        title={intl.formatMessage({ id: editingVenue ? "gestion.venues.edit" : "gestion.venues.create" })}
        onClose={() => {
          setVenueDialogOpen(false);
          setEditingVenue(null);
        }}
        onSubmit={handleSaveVenue}
      />
    </Box>
  );
};

export default GestionVenues;
